import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/content";
import ImageField from "./ImageField";

const inputCls = "w-full border border-navy/20 bg-white px-3.5 py-3 text-sm text-navy focus:border-gold";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setForm({ ...r.data, notify_email: "" }))
      .catch(() => setForm(null));
  }, []);

  if (!form) return <p className="text-sm text-inkgray">Carregando…</p>;
  const c = form.contact || {};
  const setContact = (k) => (e) => setForm({ ...form, contact: { ...c, [k]: e.target.value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        logo_url: form.logo_url,
        logo_footer_url: form.logo_footer_url,
        contact: form.contact,
      });
      refresh();
      toast.success("Configurações salvas.");
    } catch (e) {
      toast.error("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="font-display text-3xl text-navy">Configurações</h1>
          <p className="mt-2 text-sm text-inkgray">Logotipo e dados de contato exibidos no site.</p>
        </div>
        <button
          data-testid="admin-settings-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2 disabled:opacity-60"
        >
          <Save size={14} /> {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="border border-navy/10 bg-white p-6 space-y-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-deep">Logotipo</p>
          <ImageField testid="admin-logo-light" label="Logotipo (fundo claro)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
          <ImageField testid="admin-logo-dark" label="Logotipo (fundo azul-marinho — rodapé)" value={form.logo_footer_url} onChange={(v) => setForm({ ...form, logo_footer_url: v })} />
        </div>

        <div className="border border-navy/10 bg-white p-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-deep">Dados de contato</p>
          <div className="mt-5 space-y-4">
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Telefone e WhatsApp (exibido)</p>
              <input className={inputCls} data-testid="admin-contact-phone" value={c.phone || ""} onChange={setContact("phone")} />
            </div>
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Número do WhatsApp (só dígitos, com DDI)</p>
              <input className={inputCls} data-testid="admin-contact-whatsapp" value={c.whatsapp || ""} onChange={setContact("whatsapp")} />
            </div>
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">E-mail</p>
              <input className={inputCls} data-testid="admin-contact-email" value={c.email || ""} onChange={setContact("email")} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Instagram (URL)</p>
                <input className={inputCls} data-testid="admin-contact-instagram" value={c.instagram || ""} onChange={setContact("instagram")} />
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Instagram (exibido)</p>
                <input className={inputCls} data-testid="admin-contact-instagram-handle" value={c.instagram_handle || ""} onChange={setContact("instagram_handle")} />
              </div>
            </div>
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">LinkedIn (URL)</p>
              <input className={inputCls} data-testid="admin-contact-linkedin" value={c.linkedin || ""} onChange={setContact("linkedin")} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
